import React from 'react'
import {
  useDisclosure,
  Heading,
  Skeleton,
  Flex,
  Avatar,
  VStack,
  HStack,
  Text,
  Box,
  SimpleGrid,
  Badge,
  Image,
  IconButton,
  Menu,
  MenuButton,
  MenuList,
  MenuItem
} from '@chakra-ui/react'
import { useTranslation } from 'react-i18next'
import AlertModal from 'components/AlertModal'
import { SecondaryButton, PrimaryButton } from 'components/Buttons'
import PropertyStatInfos from 'components/Property/PropertyStatInfos'
import {
  Property,
  Enum_Property_Publish_State,
  Maybe,
  Agent,
  useChangePropertyAgentsMutation,
  Enum_Agent_Status
} from 'generated/graphql'
import moment from 'moment'
import ModalWrap from 'components/ModalWrap'
import AddPropertyForm from '../AddPropertyForm'
import PropertyAgentManager from '../AddPropertyForm/PropertyAgentManager'
import { amountFormatter, formatPropertyTitle, getColorPerStatus } from 'utils'
import { useGeolocation } from 'hooks'
import { useAuthContext } from 'context/AuthProvider'
import { MdMoreVert } from 'react-icons/md'
import { theme } from 'theme'
import CustomTooltip from 'components/CustomTooltip'

const PropertyCard: React.FC<{
  property: Maybe<Property>
  loading?: boolean
  isFocused?: boolean
  onClick?: () => void
  inline?: boolean
  refetchAll?: () => Promise<void>
  onConfirm?: (id: Maybe<Property>) => Promise<void>
}> = ({ property, loading, onConfirm, refetchAll, isFocused, onClick, inline }) => {
  const { currency } = useGeolocation()
  const { isAdmin } = useAuthContext()

  const modalRef = React.useRef<any>(null)
  const [changePropertyAgent] = useChangePropertyAgentsMutation({
    onCompleted: async () => {
      await refetchAll?.()
    },
    onError: (error) => {
      console.log(error)
    }
  })
  const { t, i18n } = useTranslation()
  const translator = i18n as any
  const lang = translator.language as string
  const { isOpen: isOpenPublish, onClose: onClosePublish, onOpen: onOpenPublish } = useDisclosure()
  const {
    isOpen: isOpenUnpublish,
    onClose: onCloseUnpublish,
    onOpen: onOpenUnpublish
  } = useDisclosure()
  const {
    isOpen: isAddPropertyEditOpen,
    onClose: onAddPropertyEditClose,
    onOpen: onAddPropertyEditOpen
  } = useDisclosure()

  const isAgentActive = property?.agent?.status === Enum_Agent_Status.Active

  const options = () => (
    <>
      <HStack
        spacing={7}
        justifyContent={isAddPropertyEditOpen ? 'flex-end' : 'flex-start'}
        w="100%"
        py={1}
      >
        <HStack>
          {!isAddPropertyEditOpen && (
            <SecondaryButton onClick={onAddPropertyEditOpen}>Edit</SecondaryButton>
          )}
          {property?.publish_state === Enum_Property_Publish_State.Active ||
          property?.publish_state === Enum_Property_Publish_State.UnderReview ? (
            <PrimaryButton onClick={onOpenUnpublish} colorScheme="red">
              Unpublish
            </PrimaryButton>
          ) : (
            <PrimaryButton colorScheme="green" isDisabled={!isAgentActive} onClick={onOpenPublish}>
              Publish
            </PrimaryButton>
          )}
        </HStack>
      </HStack>
      <VStack
        spacing={0}
        textAlign={isAddPropertyEditOpen ? 'right' : 'left'}
        alignItems={isAddPropertyEditOpen ? 'flex-end' : 'flex-start'}
      >
        {!isAgentActive && (
          <Text color="red.600" fontWeight="bold" fontSize="x-small">
            Agent is not active
          </Text>
        )}
        <Text color="green.600" fontSize="x-small">
          Last Updated {moment(property?.updated_at).calendar()}
        </Text>
        {property?.publish_state === Enum_Property_Publish_State.UnderReview && (
          <CustomTooltip label="This property will be published once it has been approved">
            <Text color="orange.600" pt={4} fontWeight="bold" fontSize="xs">
              Property is under review
            </Text>
          </CustomTooltip>
        )}
      </VStack>
    </>
  )

  return (
    <>
      <ModalWrap
        isOpen={isAddPropertyEditOpen}
        ref={modalRef}
        rightElement={
          <PropertyAgentManager
            agent={property?.agent as Agent}
            onEdit={() => {
              onAddPropertyEditClose()
            }}
            onReplace={async (id) => {
              await changePropertyAgent({
                variables: {
                  property: property?.id || '',
                  agent: id || ''
                }
              })
              await refetchAll?.()
            }}
          />
        }
        onClose={onAddPropertyEditClose}
        title=""
      >
        <Flex justify="space-between">
          <VStack alignItems="flex-start">
            <Heading as="h3" fontWeight="extrabold">
              Edit Property
            </Heading>
            <Text color="gray.600" fontSize="xs">
              Edit the property details
            </Text>
          </VStack>
          <VStack alignItems="flex-start" spacing={0}>
            {options()}
          </VStack>
        </Flex>
        <AddPropertyForm
          property={property}
          modalRef={modalRef}
          onSubmit={async () => {
            onAddPropertyEditClose()
            await refetchAll?.()
          }}
        />
      </ModalWrap>
      <AlertModal
        activatorStatus="deactivate"
        onConfirm={async () => {
          await onConfirm?.(property)
          onCloseUnpublish()
        }}
        loading={loading}
        isOpen={isOpenUnpublish}
        onClose={onCloseUnpublish}
        confirmButtonText="Unpublish"
        title="Unpublish Property"
      >
        <Heading fontSize="sm">
          Are you sure you want to unpushing this property from the platform?
        </Heading>
        <Text fontSize="xs" mt={6}>
          By removing a property from the platform, it will no longer be able to be viewed by
          potential buyers.
        </Text>
      </AlertModal>
      <AlertModal
        activatorStatus="activate"
        onConfirm={async () => {
          await onConfirm?.(property)
          onClosePublish()
        }}
        loading={loading}
        isOpen={isOpenPublish}
        onClose={onClosePublish}
        confirmButtonText="Publish"
        title="Publish Property"
      >
        <Heading fontSize="sm">
          Are you sure you want to pushing this property on the platform?
        </Heading>
        <Text fontSize="xs" mt={6}>
          By publishing a property from the platform, it will be able to be viewed by potential
          buyers.
        </Text>
      </AlertModal>
      <Skeleton
        isLoaded={!loading}
        boxShadow={isFocused ? theme.boxShadow2 : 'none'}
        bg={isFocused ? 'gray.50' : 'white'}
        rounded="lg"
        height="auto"
        onClick={onClick}
        border="3px solid"
        borderColor={isFocused ? 'orange.100' : inline ? 'transparent' : 'gray.100'}
        cursor="pointer"
      >
        <HStack
          px={inline ? 6 : 10}
          py={4}
          borderBottom="1px solid"
          borderBottomColor="gray.100"
          minW={24}
        >
          <HStack spacing={4}>
            {property?.photos?.length ? (
              <Image
                src={property?.photos?.[0]?.formats?.thumbnail?.url}
                alt={property?.photos?.[0]?.name}
                boxSize={14}
                rounded="xl"
                objectFit="cover"
              />
            ) : (
              <Flex
                bg="gray.100"
                rounded="xl"
                boxSize={14}
                alignItems="center"
                justifyContent="center"
              >
                <Text fontSize="x-small" color="gray.500">
                  No image
                </Text>
              </Flex>
            )}
            <Heading fontWeight="semibold" size="xs">
              {formatPropertyTitle(property as Property, t, lang)}
            </Heading>
            <Badge
              colorScheme={getColorPerStatus(property?.publish_state || '')}
              rounded="xl"
              px={3}
              py={1}
              textTransform="capitalize"
              fontSize="xx-small"
            >
              {property?.publish_state?.replace('_', ' ')}
            </Badge>
          </HStack>
          <Menu placement="bottom-end">
            <MenuButton as={IconButton} bg="transparent" rounded="full" icon={<MdMoreVert />} />

            <MenuList py={0} rounded="xl" fontSize="xs">
              <MenuItem py={2} onClick={onAddPropertyEditOpen}>
                Edit
              </MenuItem>

              {property?.publish_state === Enum_Property_Publish_State.Active ||
              property?.publish_state === Enum_Property_Publish_State.UnderReview ? (
                <MenuItem color="red.500" py={2} onClick={onOpenUnpublish}>
                  Unpublish
                </MenuItem>
              ) : (
                <MenuItem
                  color="green.500"
                  py={2}
                  onClick={onOpenPublish}
                  isDisabled={!isAgentActive}
                >
                  Publish
                </MenuItem>
              )}
            </MenuList>
          </Menu>
        </HStack>
        <SimpleGrid columns={2} px={inline ? 6 : 10} py={4} spacing={4}>
          <VStack spacing={0} alignItems="flex-start">
            <Text fontSize="xs" color="gray.600">
              Price
            </Text>
            <Text fontWeight="extrabold">{amountFormatter(property?.price || 0, currency)}</Text>
          </VStack>
          <VStack spacing={0} alignItems="flex-start">
            <Text fontSize="xs" color="gray.600">
              Street Address
            </Text>
            <Text fontSize="xs" fontWeight="semibold">
              {property?.street_address}
            </Text>
          </VStack>
          <VStack spacing={0} alignItems="flex-start">
            <Text fontSize="xs" color="gray.600">
              Property Id
            </Text>
            <Text fontSize="xs" fontWeight="semibold">
              {property?.reference_number}
            </Text>
          </VStack>
          <VStack spacing={0} alignItems="flex-start">
            <Text fontSize="xs" color="gray.600">
              Created On
            </Text>
            <Text fontSize="xs" fontWeight="semibold">
              {moment(property?.created_at).calendar()}
            </Text>
          </VStack>
        </SimpleGrid>
        <VStack spacing={4} p={inline ? 6 : 10} alignItems="flex-start">
          <HStack spacing={4} fontSize="xs">
            <PropertyStatInfos propertyData={property as Property} loading={loading ?? false} />
          </HStack>
          <HStack spacing={6} justifyContent="space-between" width="100%">
            <Box>{options()}</Box>
            {property?.agent && isAdmin ? (
              <HStack>
                <Avatar
                  name={property?.agent?.name as string}
                  size="sm"
                  src={property?.agent?.photo?.formats?.thumbnail?.url}
                />
                <VStack spacing={0} alignItems="flex-start">
                  <Text fontSize="sm" fontWeight="semibold">
                    {property?.agent?.name}
                  </Text>
                  <Text fontSize="x-small">{property?.agent?.phone_number}</Text>
                </VStack>
              </HStack>
            ) : (
              <></>
            )}
          </HStack>
        </VStack>
      </Skeleton>
    </>
  )
}
PropertyCard.defaultProps = {
  loading: false
}

export default PropertyCard
